import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHero } from "@/components/site/shared";
import { CATEGORIES, PRODUCTS, PRODUCT_IDS } from "@/data/site";

export default function Products() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");

  const catParam = searchParams.get("cat");
  const activeCat = CATEGORIES.some((c) => c.id === catParam) ? catParam : "all";

  const setCat = (id) => setSearchParams(id === "all" ? {} : { cat: id });

  const q = query.trim().toLowerCase();
  const filtered = PRODUCT_IDS.filter((pid) => {
    const p = PRODUCTS[pid];
    if (activeCat !== "all" && p.category !== activeCat) return false;
    if (!q) return true;
    const hay = [
      t(`products.${pid}.name`),
      t(`products.${pid}.tagline`),
      t(`categories.${p.category}.title`),
      ...p.specs.map((s) => s.value),
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });

  const filterBtnCls = (active) =>
    `border px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${
      active
        ? "bg-foreground border-foreground text-background"
        : "bg-background border-border hover:border-foreground"
    }`;

  return (
    <div data-testid="products-page">
      <PageHero kicker={t("productsPage.kicker")} title={t("productsPage.title")} subtitle={t("productsPage.subtitle")} testId="products-hero" />

      <section className="py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Category filter tabs */}
          <div className="flex flex-wrap gap-3" data-testid="products-category-filters">
            <button onClick={() => setCat("all")} className={filterBtnCls(activeCat === "all")} data-testid="products-filter-all">
              {t("productsPage.filterAll")}
            </button>
            {CATEGORIES.map((cat) => (
              <button key={cat.id} onClick={() => setCat(cat.id)} className={filterBtnCls(activeCat === cat.id)} data-testid={`products-filter-${cat.id}`}>
                {t(`categories.${cat.id}.title`)}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative mt-6">
            <Search className="absolute start-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("productsPage.searchPh")}
              className="rounded-none h-14 ps-12 text-base"
              data-testid="products-search-input"
            />
          </div>

          {/* Results */}
          {filtered.length === 0 ? (
            <p className="mt-16 text-sm font-medium uppercase tracking-[0.3em] text-muted-foreground" data-testid="products-no-match">
              {t("productsPage.noMatch")}
            </p>
          ) : (
            <div className="mt-10 grid md:grid-cols-2 gap-6" data-testid="products-grid">
              {filtered.map((pid) => {
                const p = PRODUCTS[pid];
                return (
                  <Link to={`/products/${pid}`} key={pid} className="group border bg-card flex flex-col sm:flex-row overflow-hidden" data-testid={`product-card-${pid}`}>
                    <div className="sm:w-2/5 aspect-video sm:aspect-auto overflow-hidden shrink-0">
                      <img src={p.image} alt={t(`products.${pid}.name`)} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="p-6 flex flex-col justify-between">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-primary">{t(`categories.${p.category}.title`)}</p>
                        <h3 className="mt-1.5 font-heading text-xl font-bold group-hover:text-primary transition-colors">{t(`products.${pid}.name`)}</h3>
                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(`products.${pid}.tagline`)}</p>
                      </div>
                      <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                        {t("categories.view")}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
